import React from "react";
import { Select } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(quarterOfYear);

export type TimeRange = "today" | "7days" | "30days" | "quarter" | "all";

export function getDateRange(range: TimeRange): { createdAfter?: string } {
  const now = dayjs();
  switch (range) {
    case "today":
      return { createdAfter: now.startOf("day").toISOString() };
    case "7days":
      return {
        createdAfter: now.subtract(7, "day").startOf("day").toISOString(),
      };
    case "30days":
      return {
        createdAfter: now.subtract(30, "day").startOf("day").toISOString(),
      };
    case "quarter":
      return { createdAfter: now.startOf("quarter").toISOString() };
    case "all":
    default:
      return {};
  }
}

interface TimeRangePickerProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  compact?: boolean;
}

export const TimeRangePicker: React.FC<TimeRangePickerProps> = ({
  value,
  onChange,
  compact,
}) => {
  const { t } = useTranslation();

  const options = [
    { value: "today" as const, label: t("timeRange.today", "Today") },
    { value: "7days" as const, label: t("timeRange.7days", "Last 7 Days") },
    { value: "30days" as const, label: t("timeRange.30days", "Last 30 Days") },
    {
      value: "quarter" as const,
      label: t("timeRange.quarter", "This Quarter"),
    },
    { value: "all" as const, label: t("timeRange.all", "All Time") },
  ];

  return (
    <div data-testid="time-range-picker" style={{ display: "inline-block" }}>
      <Select
        value={value}
        onChange={onChange}
        options={options}
        style={{ minWidth: compact ? 120 : 150 }}
        suffixIcon={<CalendarOutlined />}
        variant="borderless"
      />
    </div>
  );
};
